/**
 * useInvitations + invitation-management mutations — extracted from
 * `use-users.ts` per issue #134 (split monolithic Users hook).
 *
 * Covers:
 *   - Invitation list: useInvitations, InvitationRow
 *   - Per-user pending invitation lookup: useUserPendingInvitation
 *   - Invitation mutations: useResendInvitation, useRevokeInvitation
 *
 * USR-F1-T2: the row and list shapes are `z.infer` of the shared schema, and
 * the list asks the server for its bucket, search, page and counts. All
 * consumers continue to import from `lib/use-users` (which re-exports
 * everything here) — direct imports from `lib/use-invitations` are also
 * supported for new code.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { z } from 'zod';
import { apiClient } from './api-client';
// B2c-T2 — THE invitation status alphabet, read from the one shared
// declaration. Every shape below used to type its own five-value union whose
// last value was `cancelled`, while the screen called that state "Revoked";
// c32 renamed the stored value (founder decision 15) and the two names are one.
//
// USR-F1-T2 — and THE wire shape, from the same declaration: the route
// validates its 200 against `invitationListResponseSchema` on the way out, and
// this file derives its types from it. The hand-typed row (with a four-value
// role union narrower than the role keys the store holds) is gone.
import type {
  InvitationStatus,
  invitationResponseSchema,
  invitationListResponseSchema,
} from '@argusai/shared/schemas/invitation.schema';

// ─── W1a-05 — Invitations list + admin actions ─────────────────────────────

/**
 * Wire-shape for a single invitation row from `GET /api/v1/users/invitations`
 * — `z.infer` of the ONE shared schema.
 *
 * `tokenHash` is deliberately NOT exposed by the API — it's internal-only.
 * The raw token is only ever returned ONCE: at create-time (W1a-03 POST
 * /users → `invitation.token` field) or at resend-time (W1a-05 POST
 * /users/invitations/:id/resend → `inviteUrl` field).
 */
export type InvitationRow = z.infer<typeof invitationResponseSchema>;

/** The whole answer: one page of the bucket, its pagination, the tenant counts. */
export type InvitationsListResponse = z.infer<typeof invitationListResponseSchema>;

export interface UseInvitationsFilters {
  /** The bucket (the active tab). Omitted = every state. */
  status?: InvitationStatus;
  /** Server-side search over invitee email + inviter name / email. */
  q?: string;
  /** Exact, case-insensitive invitee address. */
  email?: string;
  page?: number;
  pageSize?: number;
  enabled?: boolean;
}

/** The querystring the list asks with — the predicate goes to the server. */
export function invitationsListUrl(filters: UseInvitationsFilters): string {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  const q = filters.q?.trim();
  if (q) params.set('q', q);
  if (filters.email) params.set('email', filters.email);
  if (filters.page) params.set('page', String(filters.page));
  if (filters.pageSize) params.set('pageSize', String(filters.pageSize));
  const qs = params.toString();
  return qs ? `/users/invitations?${qs}` : '/users/invitations';
}

/**
 * Fetch the tenant-scoped invitations list. Permission `write:users`
 * because the row carries invitee emails and inviter metadata.
 *
 * USR-F1-T2 — the server answers: the bucket (`status`), the search (`q`),
 * the page and the five badge counts all come back from ONE request. Nothing
 * here filters or counts rows.
 *
 * Query key: `['invitations', filters]` so changing status / q / page issues
 * a fresh fetch. After any mutation (resend / revoke / create-via-W1a-03),
 * invalidate `['invitations']` to refresh the page and the counts together.
 */
export function useInvitations(filters: UseInvitationsFilters = {}): {
  invitations: InvitationRow[];
  pagination: InvitationsListResponse['pagination'] | null;
  counts: InvitationsListResponse['counts'] | null;
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
} {
  // The key carries the predicate only (not `enabled`), so the page and every
  // other reader of the same question share one cache entry.
  const query: UseInvitationsFilters = {
    status: filters.status,
    q: filters.q?.trim() || undefined,
    email: filters.email,
    page: filters.page,
    pageSize: filters.pageSize,
  };
  const url = invitationsListUrl(query);

  const { data, isLoading, error, refetch } = useQuery<InvitationsListResponse>({
    queryKey: ['invitations', query],
    queryFn: () => apiClient.get<InvitationsListResponse>(url),
    enabled: filters.enabled ?? true,
    staleTime: 30_000,
  });

  return {
    invitations: data?.data ?? [],
    pagination: data?.pagination ?? null,
    counts: data?.counts ?? null,
    isLoading,
    error,
    refetch,
  };
}

export interface ResendInvitationResponse {
  id: string;
  email: string;
  expiresAt: string;
  inviteUrl: string;
  /**
   * Outcome of the most recent invitation email send attempt.
   *
   *   - `sent`     — SMTP successfully accepted the message.
   *   - `failed`   — SMTP threw or rejected. `emailError` carries a
   *                  sanitized reason. The invitation row + URL are
   *                  still valid; admin must share manually.
   *   - `disabled` — SMTP is not configured for this deployment.
   *                  Admin must share the URL manually.
   *
   * Field is server-mandatory after the W1a-08 invite-recovery PR.
   * Older deployments (pre-W1a-08) omit it — treat as `undefined`
   * and assume `disabled` to be safe.
   */
  emailDelivery?: 'sent' | 'failed' | 'disabled';
  /** Sanitized SMTP error (only present when emailDelivery === 'failed'). */
  emailError?: string;
}

/**
 * Look up the single pending invitation for a given user email.
 *
 * Background: when a sysadmin lands on `/users/:userId` for an
 * `invited`-status user, the lifecycle action buttons (Lock, Suspend,
 * Deactivate, etc.) are disabled because the invited state has no
 * transitions in the lifecycle FSM. The W1a-08 recovery UI needs the
 * invitation id to call POST /users/invitations/:id/resend → which is
 * the only way to mint a fresh URL the admin can share.
 *
 * USR-F1-T2 — THE SERVER ANSWERS. The list route takes the predicate:
 * `?status=pending&email=<address>&pageSize=1` is an exact, case-insensitive
 * match on the invitee address, newest first, so `data[0]` is the
 * latest-issued pending invitation — the one resend rotates. Permission
 * `write:users`, the same gate as the list. Accepted / declined / expired /
 * revoked invitations are not asked for: they cannot be resent (the POST
 * /resend route returns 410 for any non-pending state). If none matches,
 * returns null.
 */
export function useUserPendingInvitation(userEmail: string | undefined | null): {
  invitation: InvitationRow | null;
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
} {
  const query = { status: 'pending' as const, email: userEmail ?? undefined, pageSize: 1 };
  const { data, isLoading, error, refetch } = useQuery<InvitationsListResponse>({
    queryKey: ['invitations', query],
    queryFn: () => apiClient.get<InvitationsListResponse>(invitationsListUrl(query)),
    enabled: !!userEmail,
    staleTime: 30_000,
  });

  return { invitation: data?.data[0] ?? null, isLoading, error, refetch };
}

/**
 * Resend an invitation — wires to W1a-05's
 * `POST /users/invitations/:id/resend`.
 *
 * Server rotates the token + sets a new 7-day expiry. The OLD token is
 * silently invalidated (its hash no longer matches the row). Response
 * includes the NEW raw URL — surface this to the admin via toast +
 * copy-to-clipboard so they can manually relay until SMTP is wired
 * (F-W1a-06.2).
 *
 * Only `pending` invitations can be resent. Any other status returns 410.
 */
export function useResendInvitation() {
  const qc = useQueryClient();
  return useMutation<ResendInvitationResponse, Error, { id: string }>({
    mutationFn: ({ id }) =>
      apiClient.post<ResendInvitationResponse>(
        `/users/invitations/${id}/resend`,
        {}
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invitations'] });
    },
  });
}

/**
 * Re-invite a person whose invitation was revoked — wires to B2c-T2's
 * `POST /users/:id/reinvite`.
 *
 * ── WHY THIS HOOK EXISTS (review B2cT2-BF-01) ─────────────────────────────
 *
 * Since B2c-T1 a revoke is not a delete: the person stays on the roster in
 * `invited_revoked`. The transition table declares exactly one way back —
 * `invited_revoked → invited`, "Re-invite", on the `invitation` surface — and
 * the endpoint that walks it shipped with NO caller in the browser. Meanwhile
 * three messages told the operator to use it: the revoke confirmation ("they
 * … can be invited again"), the create-user 409 ("Re-invite them instead of
 * creating them again"), and the resend 410. `PATCH /users/:id/status` refuses
 * `to: 'invited'` (it is not a status-surface destination) and resend answers
 * 410 for a revoked row, so the address was permanently unusable through the
 * browser — a declared edge with an endpoint no screen could walk, which is
 * the same dead state one layer up.
 *
 * It is keyed by USER id, not invitation id: the revoked invitation row is
 * spent, and what this reissues is an invitation for the person. Same user,
 * same membership, same role assignments; a new token and a new 7-day expiry.
 */
export function useReinviteUser() {
  const qc = useQueryClient();
  return useMutation<ResendInvitationResponse, Error, { userId: string }>({
    mutationFn: ({ userId }) =>
      apiClient.post<ResendInvitationResponse>(
        `/users/${userId}/reinvite`,
        {}
      ),
    onSuccess: (_data, { userId }) => {
      // The roster row's state changes (`invited_revoked → invited`), so the
      // list, the person, what the table says can be done next, and the
      // invitations tab are all stale the moment this lands — the same set
      // `useUpdateUserStatus` invalidates, plus the invitation list.
      qc.invalidateQueries({ queryKey: ['invitations'] });
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['users', userId] });
      qc.invalidateQueries({ queryKey: ['users', userId, 'lifecycle'] });
      qc.invalidateQueries({ queryKey: ['users', userId, 'activity'] });
      qc.invalidateQueries({ queryKey: ['users', userId, 'audit'] });
    },
  });
}

export interface RevokeInvitationResponse {
  id: string;
  /**
   * B2c-T2 — `revoked`, the one name for this state. It was `'cancelled'`
   * here while the button, the toast and the tab all said "Revoked".
   */
  status: Extract<InvitationStatus, 'revoked'>;
}

/**
 * Revoke an invitation — wires to W1a-05's
 * `POST /users/invitations/:id/revoke`.
 *
 * Server flips status `pending → revoked` and moves the placeholder
 * person's membership to `invited_revoked` — it does NOT delete them
 * (B2c-T2). After revoke W1a-06's
 * GET /auth/invitation/:token returns 404 and POST /auth/accept-invite
 * returns 410 — the link is dead.
 *
 * Only `pending` invitations can be revoked. Any other status returns 410.
 */
export function useRevokeInvitation() {
  const qc = useQueryClient();
  return useMutation<RevokeInvitationResponse, Error, { id: string }>({
    mutationFn: ({ id }) =>
      apiClient.post<RevokeInvitationResponse>(
        `/users/invitations/${id}/revoke`,
        {}
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invitations'] });
    },
  });
}
